import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, MapPin, Download, Folder } from 'lucide-react'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedCommunity, CommunityInput, CommunityResult } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { FolderManager, FolderMoveSelect } from './FolderManager'
// import { CommunityEditModal } from './CommunityEditModal'

interface SavedCommunitiesListProps {
  onLoad: (community: { input: CommunityInput; result: CommunityResult }) => void
}

export function SavedCommunitiesList({ onLoad }: SavedCommunitiesListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  // const [editingCommunity, setEditingCommunity] = useState<SavedCommunity | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { savedCommunities, deleteCommunity, clearItems, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, updateCommunity } = useCommunityStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredCommunities = savedCommunities
  if (selectedFolderId === 'all') {
    folderFilteredCommunities = savedCommunities
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('community')
    folders.forEach(folder => {
      getItemsInFolder('community', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredCommunities = savedCommunities.filter(community => !allFolderItemIds.has(community.id))
  } else {
    const itemIds = getItemsInFolder('community', selectedFolderId)
    folderFilteredCommunities = savedCommunities.filter(community => itemIds.includes(community.id))
  }
  
  // 検索によるフィルタリング
  const filteredCommunities = folderFilteredCommunities.filter(community =>
    community.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    community.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    community.result.basicInfo.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (community.tags && community.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  )

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みコミュニティ ({savedCommunities.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したコミュニティ設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="コミュニティを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="コミュニティ"
          />

          {savedCommunities.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのコミュニティを削除しますか？')) {
                  clearItems()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="community"
                items={savedCommunities}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('community', itemId)
                  moveItemBetweenFolders('community', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* コミュニティリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredCommunities.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '検索結果がありません' : '保存済みのコミュニティがありません'}
                  </p>
                ) : (
                  filteredCommunities.map((community) => (
                    <div 
                      key={community.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(community.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === community.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{community.title}</h3>
                        {community.isFavorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{community.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{community.result.basicInfo.scale}</Badge>
                        <Badge variant="secondary" className="text-xs">
                          人口: {community.result.basicInfo.population.toLocaleString()}
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {community.result.basicInfo.atmosphere}
                      </p>
                      
                      {community.tags && community.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {community.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(community.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="community"
                          itemId={community.id}
                          currentFolderId={getItemFolder('community', community.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: community.input, result: community.result })}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      {/* <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingCommunity(community)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button> */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(community.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(community.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このコミュニティを削除しますか？')) {
                            deleteCommunity(community.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{community.title}</h3>
                        {community.isFavorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Badge variant="outline">{community.result.basicInfo.type}</Badge>
                        <Badge variant="outline">{community.result.basicInfo.scale}</Badge>
                        <Badge variant="secondary">
                          人口: {community.result.basicInfo.population.toLocaleString()}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {community.result.basicInfo.atmosphere}
                      </p>
                      
                      {community.tags && community.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {community.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(community.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="community"
                          itemId={community.id}
                          currentFolderId={getItemFolder('community', community.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad({ input: community.input, result: community.result })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(community.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        {/* <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingCommunity(community)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button> */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(community.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このコミュニティを削除しますか？')) {
                            deleteCommunity(community.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {savedCommunities.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {/* {editingCommunity && (
        <CommunityEditModal
          isOpen={!!editingCommunity}
          onClose={() => setEditingCommunity(null)}
          onSave={(updatedResult) => {
            updateCommunity(editingCommunity.id, {
              result: { ...editingCommunity.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingCommunity(null)
          }}
          communityResult={editingCommunity.result}
          title={`「${editingCommunity.title}」を編集`}
        />
      )} */}
    </div>
  )
}